import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

/**
 * Login Page Component
 *
 * Provides a login form where users can enter their email and password
 * to authenticate with the application.
 *
 * Features:
 * - Email and password input fields
 * - Error message display above the form
 * - Loading state during authentication
 * - Redirect to dashboard on successful login
 * - Tailwind CSS styling for clean, simple design
 */
export default function LoginPage() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const { signIn, loading } = useAuth();
	const navigate = useNavigate();

	/**
	 * Handle form submission
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		const result = await signIn(email, password);

		if (result.error) {
			setError(result.error);
		} else {
			// Redirect to dashboard on successful login
			navigate("/");
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
			<div className="max-w-md w-full">
				<div className="bg-white shadow-lg rounded-lg p-8">
					<div className="text-center mb-8">
						<h1 className="text-3xl font-bold text-gray-900">Mami Tracker</h1>
						<p className="text-gray-600 mt-2">Catatan Kesehatan Harian</p>
					</div>

					{error && (
						<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					)}

					<form onSubmit={handleSubmit} className="space-y-6">
						<div>
							<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
								Email
							</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								placeholder="nama@email.com"
								disabled={loading}
							/>
						</div>

						<div>
							<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
								Kata Sandi
							</label>
							<input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								placeholder="••••••••"
								disabled={loading}
								minLength={8}
							/>
						</div>

						<button
							type="submit"
							disabled={loading}
							className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{loading ? "Memproses..." : "Masuk"}
						</button>
					</form>
				</div>
			</div>
		</div>
	);
}
